import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import type {
  WebhookEndpoint,
  CreateWebhookEndpointInput,
  UpdateWebhookEndpointInput,
} from "@adva/types";

export type { WebhookEndpoint };

interface WebhookEndpointsResponse {
  webhook_endpoints: WebhookEndpoint[];
  total: number;
}

interface WebhookEndpointResponse {
  webhook_endpoint: WebhookEndpoint;
}

interface PortalUrlResponse {
  redirect_url: string;
}

export function useWebhookEndpoints() {
  return useQuery({
    queryKey: ["webhook-endpoints"],
    queryFn: () =>
      api.get<WebhookEndpointsResponse>("/core/webhooks/endpoints"),
  });
}

export function useCreateWebhookEndpoint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateWebhookEndpointInput) =>
      api.post<WebhookEndpointResponse>("/core/webhooks/endpoints", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["webhook-endpoints"] });
    },
  });
}

export function useUpdateWebhookEndpoint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: { id: string } & UpdateWebhookEndpointInput) =>
      api.patch<WebhookEndpointResponse>(
        `/core/webhooks/endpoints/${id}`,
        data,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["webhook-endpoints"] });
    },
  });
}

export function useDeleteWebhookEndpoint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<void>(`/core/webhooks/endpoints/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["webhook-endpoints"] });
    },
  });
}

export function useWebhookPortalUrl() {
  return useMutation({
    mutationFn: () => api.get<PortalUrlResponse>("/core/webhooks/portal-url"),
  });
}
